// Agentic Trading — signal-driven paper trade proposals with human-in-the-loop approval.
// Kill switch + daily limits + position size guardrails.

import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';
import { store } from '../state';
import type { Ticker } from '../state';
import { showToast } from '../toast';

interface AgentConfig {
  smBuyThreshold:  number;
  smSellThreshold: number;
  pbBetThreshold:  number;
  pbFadeThreshold: number;
  maxPositionPct:  number;
  maxDailyOrders:  number;
  sellMode:        'full' | 'trim';
  sellPct:         number;
}
interface Proposal {
  id:             string;
  ticker:         string;
  action:         'BUY' | 'SELL';
  qty:            number;
  price:          number;
  estimatedValue: number;
  smScore:        number;
  smSignal:       string;
  probability:    number;
  pbSignal:       string;
  kelly:          number;
  factors:        string[];
  generatedAt:    string;
}
interface AgentStatus {
  killed:     boolean;
  dailyCount: number;
  history:    HistoryEntry[];
  config:     AgentConfig;
}
interface HistoryEntry {
  orderId: string; ticker: string; action: string; qty: number;
  price: number; estimatedValue: number; smScore: number;
  probability: number; executedAt: string;
}

const DEFAULT_CONFIG: AgentConfig = {
  smBuyThreshold: 65, smSellThreshold: 35,
  pbBetThreshold: 0.62, pbFadeThreshold: 0.45,
  maxPositionPct: 5, maxDailyOrders: 5,
  sellMode: 'full', sellPct: 25,
};

export class AgenticTradingTab implements Tab {
  readonly id = 'agentic-trading';
  private killed    = false;
  private dailyCount = 0;
  private maxDaily  = 5;
  private proposals: Proposal[] = [];
  private equity    = 0;
  private running   = false;

  private el(id: string) { return document.getElementById(id) as HTMLElement; }
  private inp(id: string) { return document.getElementById(id) as HTMLInputElement; }
  private sel(id: string) { return document.getElementById(id) as HTMLSelectElement; }

  init(): void {
    document.getElementById('ag-kill-btn')?.addEventListener('click', () => this.toggleKill());
    document.getElementById('ag-run-btn')?.addEventListener('click', () => this.generateProposals());
    document.getElementById('ag-save-cfg')?.addEventListener('click', () => this.saveConfig());
    document.getElementById('ag-sell-mode')?.addEventListener('change', () => this.toggleSellPct());

    // Delegate approve/reject from the stable container — attached once, not per-render
    document.getElementById('ag-proposals-body')?.addEventListener('click', async (e: MouseEvent) => {
      const approveBtn = (e.target as HTMLElement).closest<HTMLElement>('[data-ag-approve]');
      const rejectBtn  = (e.target as HTMLElement).closest<HTMLElement>('[data-ag-reject]');
      if (approveBtn) await this.approve(approveBtn.dataset.agApprove!);
      if (rejectBtn)  this.reject(rejectBtn.dataset.agReject!);
    });
  }

  render(): void { this.loadStatus(); }

  private async loadStatus(): Promise<void> {
    try {
      const s = await workerFetch<AgentStatus>('/api/agent/status');
      this.killed     = s.killed;
      this.dailyCount = s.dailyCount;
      this.maxDaily   = s.config.maxDailyOrders;
      this.updateStatusBar();
      this.populateConfig(s.config);
      this.renderHistory(s.history);
    } catch (e: unknown) {
      this.el('ag-status-text').textContent = 'Error loading agent status';
    }
  }

  private updateStatusBar(): void {
    const killBtn = this.el('ag-kill-btn') as HTMLButtonElement;
    const statusEl = this.el('ag-status-text');
    const dailyEl  = this.el('ag-daily-count');

    if (this.killed) {
      killBtn.textContent = '▶ RESUME';
      killBtn.style.background = 'rgba(46,213,115,0.15)';
      killBtn.style.borderColor = 'rgba(46,213,115,0.5)';
      killBtn.style.color = 'var(--green)';
      statusEl.innerHTML = '<span style="color:var(--red);font-weight:700;">⏹ AGENT PAUSED</span>';
    } else {
      killBtn.textContent = '⏹ KILL SWITCH';
      killBtn.style.background = 'rgba(255,71,87,0.1)';
      killBtn.style.borderColor = 'rgba(255,71,87,0.4)';
      killBtn.style.color = 'var(--red)';
      statusEl.innerHTML = '<span style="color:var(--green);font-weight:700;">● AGENT ACTIVE</span>';
    }
    const over = this.dailyCount >= this.maxDaily;
    dailyEl.innerHTML = `Orders today: <span style="color:${over?'var(--red)':'var(--accent)'};font-weight:700;">${this.dailyCount}/${this.maxDaily}</span>`;
  }

  private populateConfig(cfg: AgentConfig): void {
    this.inp('ag-sm-buy').value    = String(cfg.smBuyThreshold);
    this.inp('ag-sm-sell').value   = String(cfg.smSellThreshold);
    this.inp('ag-pb-bet').value    = String(Math.round(cfg.pbBetThreshold * 100));
    this.inp('ag-pb-fade').value   = String(Math.round(cfg.pbFadeThreshold * 100));
    this.inp('ag-max-pos').value   = String(cfg.maxPositionPct);
    this.inp('ag-max-orders').value = String(cfg.maxDailyOrders);
    this.sel('ag-sell-mode').value = cfg.sellMode;
    this.inp('ag-sell-pct').value  = String(cfg.sellPct);
    this.toggleSellPct();
  }

  private toggleSellPct(): void {
    const mode = this.sel('ag-sell-mode')?.value;
    const pctRow = this.el('ag-sell-pct-row');
    if (pctRow) pctRow.style.display = mode === 'trim' ? '' : 'none';
  }

  private async toggleKill(): Promise<void> {
    try {
      const res = await workerFetch<{ killed: boolean }>('/api/agent/kill', { method: 'POST', body: '{}', headers: { 'Content-Type': 'application/json' } });
      this.killed = res.killed;
      this.updateStatusBar();
      showToast(res.killed ? 'Agent paused — no trades will execute' : 'Agent resumed', res.killed ? 'warn' : 'success');
    } catch (e: unknown) { showToast('Kill switch error', 'error'); }
  }

  private async saveConfig(): Promise<void> {
    const cfg: AgentConfig = {
      smBuyThreshold:  parseInt(this.inp('ag-sm-buy').value)   || DEFAULT_CONFIG.smBuyThreshold,
      smSellThreshold: parseInt(this.inp('ag-sm-sell').value)  || DEFAULT_CONFIG.smSellThreshold,
      pbBetThreshold:  (parseInt(this.inp('ag-pb-bet').value)  || 62) / 100,
      pbFadeThreshold: (parseInt(this.inp('ag-pb-fade').value) || 45) / 100,
      maxPositionPct:  parseFloat(this.inp('ag-max-pos').value)    || DEFAULT_CONFIG.maxPositionPct,
      maxDailyOrders:  parseInt(this.inp('ag-max-orders').value)   || DEFAULT_CONFIG.maxDailyOrders,
      sellMode:        (this.sel('ag-sell-mode').value as 'full' | 'trim') || 'full',
      sellPct:         parseInt(this.inp('ag-sell-pct').value) || DEFAULT_CONFIG.sellPct,
    };
    this.maxDaily = cfg.maxDailyOrders;
    try {
      await workerFetch('/api/agent/config', { method: 'POST', body: JSON.stringify(cfg), headers: { 'Content-Type': 'application/json' } });
      showToast('Config saved', 'success');
      this.updateStatusBar();
    } catch (e: unknown) { showToast('Failed to save config', 'error'); }
  }

  private async generateProposals(): Promise<void> {
    if (this.running) return;
    if (this.killed) { showToast('Agent is paused — resume before generating proposals', 'warn'); return; }
    if (this.dailyCount >= this.maxDaily) { showToast('Daily order limit reached', 'warn'); return; }

    const tickers = (store.get('tickers') as Ticker[])
      .map(h => (h.Ticker ?? '').toUpperCase().trim()).filter(Boolean);
    if (!tickers.length) { showToast('No portfolio tickers — sync Holdings first', 'warn'); return; }

    const alpacaKey    = localStorage.getItem('meridian_alpaca_key')    ?? '';
    const alpacaSecret = localStorage.getItem('meridian_alpaca_secret') ?? '';
    if (!alpacaKey || !alpacaSecret) { showToast('Alpaca keys required — add them in Settings', 'warn'); return; }

    this.running = true;
    const btn = this.el('ag-run-btn') as HTMLButtonElement;
    btn.disabled = true; btn.textContent = '⟳ ANALYZING…';
    this.el('ag-proposals-body').innerHTML = `<div style="padding:24px;color:var(--text3);font-family:var(--mono);font-size:12px;">Analyzing ${tickers.length} holdings · running signal model + probability engine…</div>`;

    try {
      const res = await workerFetch<{ proposals: Proposal[]; equity: number; analyzed: number; generatedAt: string }>('/api/agent/proposals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers, alpacaKey, alpacaSecret }),
        signal: AbortSignal.timeout(180000),
      });
      this.proposals = res.proposals;
      this.equity    = res.equity;
      this.el('ag-equity').textContent = `Portfolio: $${res.equity.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
      this.el('ag-analyzed').textContent = `Analyzed ${res.analyzed} holdings · ${res.proposals.length} proposal${res.proposals.length !== 1 ? 's' : ''} generated`;
      this.renderProposals();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('ag-proposals-body').innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    } finally {
      this.running = false;
      btn.disabled = false; btn.textContent = '▶ GENERATE PROPOSALS';
    }
  }

  private renderProposals(): void {
    if (!this.proposals.length) {
      this.el('ag-proposals-body').innerHTML = `<div style="padding:24px;text-align:center;color:var(--text3);font-family:var(--mono);font-size:12px;">No actionable proposals — signals don't meet thresholds or no positions to sell.</div>`;
      return;
    }

    const rows = this.proposals.map(p => {
      const ac = p.action === 'BUY' ? 'var(--green)' : 'var(--red)';
      const acBg = p.action === 'BUY' ? 'rgba(46,213,115,0.1)' : 'rgba(255,71,87,0.1)';
      return `
        <tr style="border-bottom:1px solid var(--border);">
          <td style="padding:11px 14px;">
            <span style="font-family:var(--mono);font-size:13px;font-weight:700;color:var(--accent);">${esc(p.ticker)}</span>
            <div style="font-size:10px;color:var(--text3);margin-top:2px;">${p.factors.slice(0,2).map(f=>esc(f)).join(' · ')}</div>
          </td>
          <td style="padding:11px 14px;">
            <span style="background:${acBg};border:1px solid ${ac};color:${ac};font-family:var(--mono);font-size:11px;font-weight:700;padding:3px 10px;border-radius:2px;">${p.action}</span>
          </td>
          <td style="padding:11px 14px;font-family:var(--mono);font-size:12px;">${p.qty} <span style="color:var(--text3);">@ $${p.price.toFixed(2)}</span></td>
          <td style="padding:11px 14px;font-family:var(--mono);font-size:12px;font-weight:700;color:var(--text);">$${p.estimatedValue.toLocaleString('en-US',{maximumFractionDigits:0})}</td>
          <td style="padding:11px 14px;font-family:var(--mono);font-size:12px;">
            <span style="color:${p.smScore>=65?'var(--green)':p.smScore<=35?'var(--red)':'var(--warn)'};">${p.smScore}</span>
            <span style="color:var(--text3);font-size:10px;"> SM · </span>
            <span style="color:${p.probability>=62?'var(--green)':p.probability<=45?'var(--red)':'var(--warn)'};">${p.probability}%</span>
            <span style="color:var(--text3);font-size:10px;"> prob</span>
            ${p.kelly > 0 ? `<span style="color:var(--text3);font-size:10px;"> · Kelly ${p.kelly}%</span>` : ''}
          </td>
          <td style="padding:11px 14px;">
            <div style="display:flex;gap:6px;">
              <button data-ag-approve="${esc(p.id)}" style="background:rgba(46,213,115,0.1);border:1px solid rgba(46,213,115,0.4);color:var(--green);font-family:var(--mono);font-size:11px;font-weight:700;padding:5px 12px;cursor:pointer;border-radius:2px;letter-spacing:0.08em;">✓ APPROVE</button>
              <button data-ag-reject="${esc(p.id)}" style="background:none;border:1px solid var(--border);color:var(--text3);font-family:var(--mono);font-size:11px;padding:5px 10px;cursor:pointer;border-radius:2px;">✕</button>
            </div>
          </td>
        </tr>`;
    }).join('');

    this.el('ag-proposals-body').innerHTML = `
      <div style="overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;min-width:700px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border);">
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Ticker</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Action</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Qty</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Est. Value</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Signals</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Decision</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      </div>
      <div style="font-size:10px;color:var(--text3);margin-top:8px;padding:0 2px;">Paper trading only · Orders execute as market orders on Alpaca paper account · Approve individually</div>`;
  }

  private async approve(id: string): Promise<void> {
    const proposal = this.proposals.find(p => p.id === id);
    if (!proposal) return;

    const btn = document.querySelector<HTMLButtonElement>(`[data-ag-approve="${id}"]`);
    if (btn) { btn.disabled = true; btn.textContent = '…'; }

    try {
      const res = await workerFetch<{ ok: boolean; orderId: string; dailyCount: number }>('/api/agent/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposal }),
        signal: AbortSignal.timeout(15000),
      });
      this.dailyCount = res.dailyCount;
      this.updateStatusBar();
      showToast(`✓ ${proposal.action} ${proposal.qty} ${proposal.ticker} — Order ${res.orderId.slice(0, 8)}…`, 'success');
      this.proposals = this.proposals.filter(p => p.id !== id);
      this.renderProposals();
      this.loadStatus(); // refresh history
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Order failed: ${msg}`, 'error');
      if (btn) { btn.disabled = false; btn.textContent = '✓ APPROVE'; }
    }
  }

  private reject(id: string): void {
    this.proposals = this.proposals.filter(p => p.id !== id);
    this.renderProposals();
  }

  private renderHistory(history: HistoryEntry[]): void {
    if (!history.length) {
      this.el('ag-history-body').innerHTML = `<div style="padding:16px;color:var(--text3);font-family:var(--mono);font-size:11px;">No trades executed yet.</div>`;
      return;
    }
    this.el('ag-history-body').innerHTML = `
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr style="border-bottom:1px solid var(--border);">
          ${['Time','Ticker','Action','Qty','Value','SM','Prob'].map(h=>`<th style="padding:5px 10px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.08em;">${h}</th>`).join('')}
        </tr></thead>
        <tbody>${history.slice(0, 20).map(h => `
          <tr style="border-bottom:1px solid var(--border);">
            <td style="padding:5px 10px;color:var(--text3);">${timeAgo(h.executedAt)}</td>
            <td style="padding:5px 10px;font-family:var(--mono);color:var(--accent);font-weight:700;">${esc(h.ticker)}</td>
            <td style="padding:5px 10px;font-family:var(--mono);color:${h.action==='BUY'?'var(--green)':'var(--red)'};">${h.action}</td>
            <td style="padding:5px 10px;font-family:var(--mono);">${h.qty}</td>
            <td style="padding:5px 10px;font-family:var(--mono);">$${h.estimatedValue.toLocaleString('en-US',{maximumFractionDigits:0})}</td>
            <td style="padding:5px 10px;font-family:var(--mono);">${h.smScore}</td>
            <td style="padding:5px 10px;font-family:var(--mono);">${h.probability}%</td>
          </tr>`).join('')}
        </tbody>
      </table>`;
  }
}
